import dayjs from 'dayjs'
import type { TFunction } from 'i18next'

export const SALES_WINDOW_NOTIFICATION_TYPES = [
  'sales_window_auto_return_warning',
  'sales_window_auto_returned',
  'sales_window_auto_cancel_warning',
  'sales_window_auto_cancelled',
] as const

export type SalesWindowNotificationType =
  (typeof SALES_WINDOW_NOTIFICATION_TYPES)[number]

export function isSalesWindowNotificationType(
  type: string
): type is SalesWindowNotificationType {
  return (SALES_WINDOW_NOTIFICATION_TYPES as readonly string[]).includes(type)
}

const WARNING_TYPES: readonly SalesWindowNotificationType[] = [
  'sales_window_auto_return_warning',
  'sales_window_auto_cancel_warning',
]

/**
 * Formats the time left until `deadline` as a zero-padded `HH:MM` string,
 * flooring to whole minutes. Clamps to '00:00' when the deadline has
 * passed or is missing/invalid.
 */
export function formatTimeLeft(
  deadline: string | null | undefined,
  now: number = Date.now()
): string {
  if (!deadline) return '00:00'

  const deadlineMs = dayjs(deadline)
  if (!deadlineMs.isValid()) return '00:00'

  const diffMinutes = Math.floor(deadlineMs.diff(dayjs(now)) / (1000 * 60))
  if (diffMinutes <= 0) return '00:00'

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Derives the localized title/message for a sales-window notification.
 * `time` is only interpolated for the *_warning types.
 */
export function getSalesWindowNotificationText(
  t: TFunction,
  type: SalesWindowNotificationType,
  id: number,
  deadline: string | null | undefined,
  now: number = Date.now()
): { title: string; message: string } {
  const isWarning = WARNING_TYPES.includes(type)
  const title = t(`notifications.${type}.title`)
  const message = t(`notifications.${type}.message`, {
    id,
    ...(isWarning ? { time: formatTimeLeft(deadline, now) } : {}),
  })
  return { title, message }
}

export const salesWindowLink = (id: number): string =>
  `/orders/sales-window-list/${id}`
